/**
 * <page-toc data-source="#selector" data-levels="h2|h2h3">
 *   Builds a table of contents from the headings of the source element,
 *   gives headings ids when they have none, marks the heading in view with
 *   aria-current, and keeps its <details> open on desktop. Hidden when the
 *   text has fewer than two headings.
 */
if (!customElements.get('page-toc')) {
  const slug = (text) =>
    text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);

  customElements.define(
    'page-toc',
    class extends HTMLElement {
      connectedCallback() {
        const source = document.querySelector(this.dataset.source || '.page__content');
        const list = this.querySelector('ol');
        if (!source || !list) return;
        const selector = this.dataset.levels === 'h2' ? 'h2' : 'h2, h3';
        const headings = Array.from(source.querySelectorAll(selector)).filter((h) => h.textContent.trim());
        if (headings.length < 2) {
          this.hidden = true;
          return;
        }
        const links = new Map();
        headings.forEach((heading, index) => {
          if (!heading.id) {
            const base = slug(heading.textContent) || `section-${index + 1}`;
            let id = base;
            let n = 2;
            while (document.getElementById(id)) id = `${base}-${n++}`;
            heading.id = id;
          }
          const item = document.createElement('li');
          item.className = `page-toc__item page-toc__item--${heading.tagName.toLowerCase()}`;
          const link = document.createElement('a');
          link.href = `#${heading.id}`;
          link.textContent = heading.textContent.trim();
          item.append(link);
          list.append(item);
          links.set(heading, link);
        });
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                links.forEach((link) => link.removeAttribute('aria-current'));
                links.get(entry.target)?.setAttribute('aria-current', 'true');
              });
            },
            { rootMargin: '-15% 0px -70% 0px' }
          );
          headings.forEach((heading) => this.observer.observe(heading));
        }
        const details = this.querySelector('details');
        const desktop = window.matchMedia('(min-width: 56.25rem)');
        const sync = () => {
          if (details) details.open = desktop.matches;
        };
        sync();
        desktop.addEventListener('change', sync);
        this.hidden = false;
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    }
  );
}
